/**
 * `calendar_bookings.status` — eine Quelle fuer Vokabular, Beschriftung und
 * Bedeutung.
 *
 * Es gab fuenf Auffassungen davon im Code: Kalender/Store/Termin-Dialog kannten
 * `attendee|confirmed|''`, Chat und Inbox zusaetzlich `attended`, die Kundenakte
 * SCHRIEB ueber ihr Dropdown `booked` ("Gebucht"), `attended` und `noshow`, der
 * Vertragsdialog wiederum `absent`. Eine Buchung mit `status='booked'` fiel
 * dadurch durch jede Teilnehmerliste UND durch Warteliste und Archiv: der Mensch
 * war in der Kundenakte sichtbar und im Termin spurlos weg.
 *
 * Festgelegt (2026-09-01) sind die fuenf Werte in BOOKING_STATUSES. Alles
 * andere ist Altbestand: es wird gelesen und beschriftet, aber nicht mehr
 * geschrieben.
 */

/** Die Werte, die geschrieben werden. Nichts sonst gehoert in die Spalte. */
export const BOOKING_STATUSES = [
    'attendee',
    'waitlist',
    'cancelledbyclient',
    'cancelledbycompany',
    'noshow',
] as const

export type BookingStatus = typeof BOOKING_STATUSES[number]

/** Beschriftung der fuenf gueltigen Werte — so heissen sie in jeder Oberflaeche. */
export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
    attendee:           'Teilnehmer',
    waitlist:           'Warteliste',
    cancelledbyclient:  'Abgesagt durch Kunden',
    cancelledbycompany: 'Abgesagt durch Studio',
    noshow:             'Nicht erschienen',
}

/**
 * Altwerte aus dem Bestand. Sie stehen nicht mehr zur Auswahl, muessen aber
 * lesbar bleiben — und erkennbar als das, was sie sind, damit niemand sie fuer
 * eine gueltige Option haelt.
 */
const LEGACY_STATUS_LABELS: Record<string, string> = {
    booked:    'Teilnehmer (Altwert „booked")',
    confirmed: 'Teilnehmer (Altwert „confirmed")',
    attended:  'Teilgenommen (Altwert)',
    cancelled: 'Abgesagt (Altwert)',
    absent:    'Nicht erschienen (Altwert „absent")',
}

const clean = (status: unknown): string => String(status ?? '').trim().toLowerCase()

/**
 * Status, die einen Teilnehmer NICHT in der Teilnehmerliste zeigen.
 *
 * Bewusst eine BLACKLIST: ein Status musste frueher BEKANNT sein, um sichtbar zu
 * sein; jeder neue oder vertippte Wert liess einen Teilnehmer lautlos
 * verschwinden. Umgekehrt ist der Fehler harmlos — ein unbekannter Status zeigt
 * eine Person zu viel, statt eine zu verschlucken.
 *
 * Archiviert wird weiterhin separat ueber `archive` geprueft; diese Spalte ist
 * eindeutig und gehoert nicht in den Status.
 *
 * `noshow`/`absent` und `attended` zaehlen bewusst MIT: alle drei beschreiben
 * den Ausgang einer echten Buchung. Fuer die Kapazitaet ist das folgenlos, weil
 * in der Zukunft niemand diese Zustaende traegt.
 */
const NON_ATTENDEE_STATUSES = new Set([
    'waitlist',
    'cancelled',
    'cancelledbyclient',
    'cancelledbycompany',
])

/**
 * Zaehlt diese Buchung als Teilnehmer des Termins?
 * Leerer/fehlender Status = ja (Altbestand vor 2018 hat kein Statusfeld).
 */
export function isAttendeeStatus(status: unknown): boolean {
    if (!status) return true
    return !NON_ATTENDEE_STATUSES.has(clean(status))
}

/** Steht diese Buchung auf der Warteliste? */
export function isWaitlistStatus(status: unknown): boolean {
    return clean(status) === 'waitlist'
}

/** Wurde diese Buchung abgesagt (von Kunde oder Studio)? */
export function isCancelledStatus(status: unknown): boolean {
    const s = clean(status)
    return s === 'cancelled' || s === 'cancelledbyclient' || s === 'cancelledbycompany'
}

/** Der Kunde ist nicht erschienen. `absent` ist der Altwert desselben Zustands. */
export function isNoShowStatus(status: unknown): boolean {
    const s = clean(status)
    return s === 'noshow' || s === 'absent'
}

/**
 * Hat das Training stattgefunden? Kein eigener Status mehr — `attended` war ein
 * Altwert. Ein Training gilt als absolviert, wenn es weder abgesagt noch
 * Warteliste noch ein Nichterscheinen ist.
 */
export function isCompletedStatus(status: unknown): boolean {
    return !isCancelledStatus(status) && !isWaitlistStatus(status) && !isNoShowStatus(status)
}

/** Steht der Wert als gueltige Option zur Auswahl? Altwerte: nein. */
export function isKnownBookingStatus(status: unknown): boolean {
    return (BOOKING_STATUSES as readonly string[]).includes(clean(status))
}

/**
 * Beschriftung eines beliebigen Statuswerts — gueltig, Altbestand oder leer.
 * Ein voellig unbekannter Wert kommt roh zurueck, damit er in der Oberflaeche
 * nicht verschwindet.
 */
export function getBookingStatusLabel(status: unknown): string {
    const s = clean(status)
    if (!s) return 'Teilnehmer (ohne Status)'
    return BOOKING_STATUS_LABELS[s as BookingStatus] || LEGACY_STATUS_LABELS[s] || String(status)
}
